import { INTELLIGENCE_DEV_PRICE_MODEL } from "@/constants/user";
import env from "@/env";
import {
  AgentDetail,
  AgentDetailResponse,
  APIKeyItem,
  APIKeyRequest,
  APIKeyResponse,
  AttestationRequest,
  AttestationResponse,
  ChatResponse,
  IntelligenceInferenceResponseType,
  IntelligenceInferenceStatsType,
  IntelligenceInferenceType,
  IntelligenceUsageType,
  MessageResponse,
  MessageType,
  ModelDetail,
  ModelDetailResponse,
  QuotaDialogMode,
  SecretItem,
  SecretRequest,
  Stats,
  StatsResponse
} from "@/types/Intelligence";
import {
  ChunkResponse,
  ExtractionStatus,
  IngestionStatus,
  RagChunk,
  RagDocumentResponse,
  RagFileDocument
} from "@/types/rag";
import { SubscriptionStatusResponse } from "@/types/user";
import {
  GetFilesResponse,
  GetFoldersResponse,
  WorkflowDetails,
  WorkflowMetadata,
  WorkflowToolType
} from "@/types/workflow";
import { executeAPIRequest, GUEST_USER_ID_HEADER } from "@/utils/api";
import { generate32DigitHexNonce } from "@/utils/intelligence";
import { getGuestUserHeaders, getGuestUserId } from "@/utils/user";
import { AxiosProgressEvent } from "axios";
import { stringify } from "qs";
import { create } from "zustand";

export type PaginationType = {
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
};

type useIntelligenceStoreProps = {
  models: ModelDetail[];
  keys: APIKeyItem[];
  agents: AgentDetail[];
  workflowTools: WorkflowToolType[];
  secrets: SecretItem[];
  isModelsLoading: boolean;
  isKeysLoading: boolean;
  isAgentsLoading: boolean;
  isToolsLoading: boolean;
  isSecretsLoading: boolean;
  isChatsLoading: boolean;
  newMessage: string;
  chats: ChatResponse[];
  documents: RagFileDocument[];
  setDocuments: (docs: RagFileDocument[]) => void;
  isGenerating: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ragSettings: any;
  intelligenceUsage: IntelligenceUsageType | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setRagSettings: (val: any) => void;
  setIsGenerating: (value: boolean) => void;
  fetchModels: (refresh?: boolean) => Promise<ModelDetail[]>;
  fetchModelDetail: (id: string) => Promise<ModelDetail>;
  createChat: (modelId: string, title: string) => Promise<string>;
  fetchMessages: (
    chatId: string,
    page?: number
  ) => Promise<{ data: MessageType[]; pagination: PaginationType }>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sendMessage: (chatId: string, content: string) => Promise<any>;
  fetchChats: (modelId: string) => void;
  setNewMessage: (value: string) => void;
  deleteChat: (chatId: string) => Promise<{ message: string; status: string }>;
  createKey: (data: APIKeyRequest) => Promise<APIKeyResponse>;
  fetchKeys: () => void;
  updateKey: (id: string, data: APIKeyRequest) => Promise<string>;
  deleteKey: (id: string) => Promise<string>;
  fetchAgents: () => Promise<AgentDetail[]>;
  fetchWorkflowTools: () => Promise<WorkflowToolType[]>;
  fetchSecrets: (forceRefresh?: boolean) => Promise<SecretItem[]>;
  createSecret: (data: SecretRequest) => Promise<SecretItem>;
  deleteSecret: (id: string) => Promise<void>;
  fetchStats: () => Promise<Stats>;
  validateWorkflow: (yamlText: string) => Promise<boolean>;
  runYamlWorkflow: (
    yamlText: string,
    abortController?: AbortController | undefined // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => Promise<any>;
  getWorkflowDetails: (id: string) => Promise<WorkflowDetails>;
  createNewWorkflow: (
    name: string,
    yamlFile: File,
    metadata: WorkflowMetadata
  ) => Promise<WorkflowDetails>;
  updateWorkflow: (
    id: string,
    { yamlFile, name, metadata }: { yamlFile?: File; name?: string; metadata?: WorkflowMetadata }
  ) => Promise<null>;
  deleteWorkflow: (id: string) => Promise<null>;
  // workflow files and folder related methods
  getWorkflowFolders: () => Promise<GetFoldersResponse>;
  getWorkflowFiles: () => Promise<GetFilesResponse>;
  createRagChat: (title: string) => Promise<string>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sendRagMessage: (query: string) => Promise<any>;
  addDocuments: (
    formData: FormData,
    onUploadProgress?: ((progressEvent: AxiosProgressEvent) => void) | undefined,
    headers?: Record<string, unknown>
  ) => Promise<string>;
  fetchDocuments: (ids?: string, offset?: number, limit?: number) => Promise<RagFileDocument[]>;
  fetchDocumentChunks: (
    id: string,
    offset?: number,
    limit?: number
  ) => Promise<{
    results: RagChunk[];
    totalEntries: number;
  }>;
  downloadDocument: (id: string) => Promise<Blob>;
  deleteDocument: (id: string) => Promise<void>;
  extractDocument: (id: string) => Promise<void>;
  isQuotaLimitDialogOpen: boolean;
  quotaLimitDialogMode: QuotaDialogMode;
  setIsQuotaLimitDialogOpen: (value: boolean, mode?: QuotaDialogMode) => void;
  isPrivateDetailsPanelOpen: boolean;
  setIsPrivateDetailsPanelOpen: (open: boolean) => void;
  selectedMessageId: string | null;
  setSelectedMessageId: (id: string | null) => void;
  fetchIntelligenceInferenceStats: () => Promise<IntelligenceInferenceStatsType>;
  fetchIntelligenceInferences: () => Promise<IntelligenceInferenceType[]>;
  fetchIntelligenceUsage: () => Promise<IntelligenceUsageType>;
  getTotalCreditsUsed: (
    intelligenceUsage: IntelligenceUsageType,
    subscription: SubscriptionStatusResponse["data"]
  ) => number;
  fetchAttestation: (data: AttestationRequest) => Promise<AttestationResponse>;
  isCurrentChatConfidential: boolean;
  setIsCurrentChatConfidential: (value: boolean) => void;
  lastGeneratedKey: string | null;
  setLastGeneratedKey: (key: string | null) => void;
};

export const useIntelligenceStore = create<useIntelligenceStoreProps>((set, get) => {
  const url = env.API_URL_INTELLIGENCE;
  const workflowUrl = env.API_URL_INTELLIGENCE.replace("/v1", "") + "/api/v1";
  const ragUrl = env.API_URL_INTELLIGENCE.replace("/v1", "") + "/api/r2r/v3";
  const secretsUrl = env.API_URL_INTELLIGENCE.replace("/v1", "") + "/v1/secrets/";
  return {
    isModelsLoading: false,
    isKeysLoading: false,
    isAgentsLoading: false,
    isToolsLoading: false,
    isSecretsLoading: false,
    isChatsLoading: false,
    isGenerating: false,
    isQuotaLimitDialogOpen: false,
    quotaLimitDialogMode: "no-io-credits",
    isPrivateDetailsPanelOpen: false,
    setIsPrivateDetailsPanelOpen: (open) => set({ isPrivateDetailsPanelOpen: open }),
    isCurrentChatConfidential: false,
    setIsCurrentChatConfidential: (value) => set({ isCurrentChatConfidential: value }),
    selectedMessageId: null,
    setSelectedMessageId: (id) => set({ selectedMessageId: id }),
    models: [],
    keys: [],
    agents: [],
    workflowTools: [],
    secrets: [],
    newMessage: "",
    chats: [],
    intelligenceUsage: null,
    lastGeneratedKey: null,
    setLastGeneratedKey: (key) => {
      set({ lastGeneratedKey: key });
    },
    setNewMessage: (value) => {
      set({ newMessage: value });
    },
    setIsGenerating: (isGenerating) => {
      set({ isGenerating });
    },
    ragSettings: {
      search_settings: {
        use_hybrid_search: false,
        use_semantic_search: true,
        filters: {},
        //vector
        chunk_settings: {
          index_measure: "cosine_distance",
          enabled: true,
          probes: 10,
          ef_search: 40
        },
        graph_settings: { enabled: false }
      },
      hybrid_settings: {
        full_text_weight: 1,
        semantic_weight: 5,
        full_text_limit: 200,
        rrf_k: 50
      },
      rag_generation_config: {
        max_tokens_to_sample: 16000,
        stream: true,
        extended_thinking: true,
        thinking_budget: 4096,
        temperature: 0.2
      }
    },
    documents: [],
    setDocuments: (documents) => {
      set({ documents });
    },
    setRagSettings: (val) => {
      set({ ragSettings: val });
    },
    fetchModels: async (refresh) => {
      const { models, isModelsLoading } = get();
      if (isModelsLoading) {
        return models;
      }
      if (models.length && !refresh) {
        return models;
      }
      set({ isModelsLoading: true });
      let modelsList: ModelDetail[] = [];

      try {
        const response = await executeAPIRequest<{
          data: ModelDetailResponse[];
          status: string;
        }>({
          method: "get",
          url: `${url}/models`,
          options: {
            replaceUrl: true,
            headers: {
              [GUEST_USER_ID_HEADER]: getGuestUserId()
            }
          }
        });
        modelsList = response.data
          .map((item) => {
            return normalizeModelDetail(item);
          })
          .sort((a, b) => {
            if (a.supportsAttestation && !b.supportsAttestation) return -1;
            if (!a.supportsAttestation && b.supportsAttestation) return 1;
            return 0;
          });
        set({ models: modelsList });
      } catch (e) {
        console.log(e);
      } finally {
        set({ isModelsLoading: false });
      }

      return modelsList;
    },
    fetchModelDetail: async (id) => {
      const response = await executeAPIRequest<ModelDetailResponse>({
        method: "get",
        url: `${url}/models/${id}`,
        options: {
          replaceUrl: true,
          headers: {
            [GUEST_USER_ID_HEADER]: getGuestUserId()
          }
        }
      });
      return normalizeModelDetail(response);
    },
    extractDocument: async (id: string) => {
      return await executeAPIRequest({
        method: "post",
        url: `${ragUrl}/documents/${id}/extract`,
        options: {
          replaceUrl: true
        }
      });
    },
    createChat: async (modelId, title) => {
      const response = await executeAPIRequest<{ chat_id: string }>({
        method: "post",
        url: `${url}/chats`,
        options: {
          axiosRetries: 0,
          replaceUrl: true,
          data: {
            llm_id: modelId,
            title
          },
          headers: {
            [GUEST_USER_ID_HEADER]: getGuestUserId()
          }
        }
      });
      return response.chat_id;
    },
    fetchMessages: async (chatId, page = 1) => {
      const guestUserHeaders = getGuestUserHeaders();
      const response = await executeAPIRequest<{
        data: MessageResponse[];
        pagination: PaginationType;
      }>({
        method: "get",
        url: `${url}/chats/${chatId}/messages?page_size=20&page=${page}`,
        options: {
          replaceUrl: true,
          headers: {
            ...guestUserHeaders
          }
        }
      });
      return {
        data: response.data.map((item) => normalizeMessage(item)).reverse(),
        pagination: response.pagination
      };
    },
    sendMessage: async (chatId, content) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response = await executeAPIRequest<any>({
        method: "post",
        url: `${url}/chats/${chatId}/messages`,
        options: {
          axiosRetries: 0,
          replaceUrl: true,
          responseType: "stream",
          publicError: true,
          adapter: "fetch",
          timeout: 600000,
          data: {
            messages: [
              {
                content: content,
                role: "user"
              }
            ],
            stream: true
          },
          headers: {
            [GUEST_USER_ID_HEADER]: getGuestUserId()
          }
        }
      });
      return response;
    },
    fetchChats: async (modelId) => {
      const { isChatsLoading } = get();
      if (isChatsLoading) {
        return;
      }
      set({ isChatsLoading: true });
      const response = await executeAPIRequest<{
        data: ChatResponse[];
      }>({
        method: "get",
        url: `${url}/chats${modelId ? `?llm_id=${modelId}` : ""}`,
        options: {
          replaceUrl: true,
          headers: {
            [GUEST_USER_ID_HEADER]: getGuestUserId()
          }
        }
      });
      const { data } = response;
      set({ chats: data, isChatsLoading: false });
    },
    deleteChat: async (chatId) => {
      const { chats } = get();

      const response = await executeAPIRequest<{
        message: string;
        status: string;
      }>({
        method: "delete",
        url: `${url}/chats/${chatId}`,
        options: {
          replaceUrl: true
        }
      });
      if (response.status === "succeeded") {
        set({ chats: chats.filter((item) => item.chat_id !== chatId) });
        return response;
      } else {
        console.error("Failed to delete chat:", response.message);
      }
      return response;
    },
    createKey: async (data) => {
      const response = await executeAPIRequest<APIKeyResponse>({
        method: "post",
        url: `/api-keys/`,
        options: {
          data: data
        }
      });
      return response;
    },
    fetchKeys: async () => {
      set({ isKeysLoading: true });
      try {
        const response = await executeAPIRequest<{ keys: APIKeyItem[] }>({
          method: "get",
          url: `/api-keys/`
        });
        set({ keys: response.keys });
      } catch (e) {
        console.log(e);
      } finally {
        set({ isKeysLoading: false });
      }
    },
    updateKey: async (id, data) => {
      const response = await executeAPIRequest<string>({
        method: "patch",
        url: `/api-keys/${id}`,
        options: {
          data
        }
      });
      return response;
    },
    deleteKey: async (id) => {
      const response = await executeAPIRequest<string>({
        method: "delete",
        url: `/api-keys/${id}`
      });
      return response;
    },
    fetchAgents: async () => {
      const { agents, isAgentsLoading } = get();
      if (agents.length > 0 || isAgentsLoading) return agents;

      set({ isAgentsLoading: true });
      try {
        const response = await executeAPIRequest<{
          agents: {
            [name: string]: AgentDetailResponse;
          };
        }>({
          method: "get",
          url: `${url}/agents/`,
          options: {
            replaceUrl: true,
            headers: {
              [GUEST_USER_ID_HEADER]: getGuestUserId()
            }
          }
        });
        const agentsMap = response.agents;
        const agentsList = Object.entries(agentsMap).map(([key, value]) =>
          normalizeAgent(value, key)
        );
        set({ agents: agentsList });
        return agentsList;
      } catch (e: unknown) {
        console.error(e);
        return [];
      } finally {
        set({ isAgentsLoading: false });
      }
    },
    fetchWorkflowTools: async () => {
      const { workflowTools, isToolsLoading } = get();
      if (workflowTools.length > 0 || isToolsLoading) return workflowTools;

      set({ isToolsLoading: true });
      try {
        const response = await executeAPIRequest<{
          data: WorkflowToolType[];
        }>({
          method: "get",
          url: `${workflowUrl}/tools`,
          options: {
            replaceUrl: true
          }
        });

        const tools = response.data.map(
          (tool) =>
            ({
              name: tool.name,
              brief: tool.brief,
              description: tool.description.trim(),
              zeroconf: tool.zeroconf,
              state_signature: tool.state_signature
            }) as WorkflowToolType
        );

        set({ workflowTools: tools });
        return tools;
      } catch (e: unknown) {
        console.error(e);
        return [];
      } finally {
        set({ isToolsLoading: false });
      }
    },
    fetchSecrets: async (forceRefresh = false) => {
      const { secrets, isSecretsLoading } = get();
      if (!forceRefresh && (secrets.length > 0 || isSecretsLoading)) return secrets;

      set({ isSecretsLoading: true });
      try {
        const response = await executeAPIRequest<{
          data: SecretItem[];
        }>({
          method: "get",
          url: secretsUrl,
          options: {
            replaceUrl: true
          }
        });
        const secretsList = response.data || [];
        set({ secrets: secretsList });
        return secretsList;
      } catch (e: unknown) {
        console.error(e);
        return [];
      } finally {
        set({ isSecretsLoading: false });
      }
    },
    createSecret: async (data) => {
      const result = await executeAPIRequest<SecretItem | { data: SecretItem }>({
        method: "post",
        url: secretsUrl,
        options: {
          replaceUrl: true,
          data
        }
      });

      get().fetchSecrets(true);
      return "data" in result ? result.data : result;
    },
    deleteSecret: async (id) => {
      await executeAPIRequest<void>({
        method: "delete",
        url: `${secretsUrl}${id}`,
        options: {
          replaceUrl: true
        }
      });
      get().fetchSecrets(true);
    },
    fetchStats: async () => {
      const response = await executeAPIRequest<StatsResponse>({
        method: "get",
        url: `${url}/stats/token-usage`,
        options: {
          replaceUrl: true
        }
      });
      return normalizeStats(response);
    },
    createRagChat: async (title) => {
      const response = await executeAPIRequest<{
        results: {
          id: string;
          created_at: string;
          user_id: string;
          name: string;
        };
      }>({
        method: "post",
        url: `${ragUrl}/conversations`,
        options: {
          replaceUrl: true,
          data: {
            name: title
          }
        }
      });
      return response.results.id;
    },
    sendRagMessage: async (query) => {
      // Create a TransformStream to process the response
      const transformStream = new TransformStream({
        transform(chunk, controller) {
          // Process each chunk here if needed
          controller.enqueue(chunk);
        }
      });

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response = await executeAPIRequest<any>({
        method: "post",
        url: `${ragUrl}/retrieval/rag`,
        options: {
          replaceUrl: true,
          responseType: "stream",
          publicError: true,
          adapter: "fetch",
          timeout: 600000,
          data: {
            query,
            ...get().ragSettings
          }
        }
      });
      // Pipe the response through the transform stream
      const streamedResponse = response?.pipeThrough(transformStream);

      return streamedResponse;
    },
    addDocuments: async (
      formData: FormData,
      onUploadProgress?: ((progressEvent: AxiosProgressEvent) => void) | undefined,
      headers?: Record<string, unknown>
    ) => {
      const response = await executeAPIRequest<{
        results: {
          message: string;
          task_id: string;
          document_id: string;
        };
      }>({
        method: "post",
        url: `${ragUrl}/documents`,
        options: {
          headers,
          onUploadProgress,
          replaceUrl: true,
          data: formData,
          transformRequest: [
            (data, headers) => {
              delete headers["Content-Type"];
              return data;
            }
          ]
        }
      });
      return response.results.message;
    },
    fetchDocumentChunks: async (id: string) => {
      const response = await executeAPIRequest<{
        results: ChunkResponse[];
        total_entries: number;
      }>({
        method: "get",
        url: `${ragUrl}/documents/${id}/chunks`,
        options: {
          replaceUrl: true
        }
      });
      return {
        results: response.results.map((chunk: ChunkResponse) => normaliseChunk(chunk)),
        totalEntries: response.total_entries
      };
    },
    fetchDocuments: async (ids?: string, offset = 0, limit = 500) => {
      const response = await executeAPIRequest<{
        results: RagDocumentResponse[];
      }>({
        method: "get",
        url: `${ragUrl}/documents?${stringify({
          offset,
          limit,
          ...(ids ? { ids } : {})
        })}`,
        options: {
          replaceUrl: true
        }
      });
      return response.results.map((doc: RagDocumentResponse) => normaliseDocument(doc));
    },
    downloadDocument: async (id: string) => {
      const response = await executeAPIRequest<{
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        headers: any;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        data: any;
      }>({
        method: "get",
        url: `${ragUrl}/documents/${id}/download`,
        options: {
          responseType: "arraybuffer",
          fullResponse: true,
          replaceUrl: true
        }
      });

      const contentType = !response.headers
        ? "application/octet-stream"
        : response.headers["content-type"];
      if (response.data instanceof Blob) {
        return response.data;
      }
      if (response.data instanceof ArrayBuffer) {
        return new Blob([response.data], { type: contentType });
      }
      return new Blob([response.data], { type: contentType });
    },
    deleteDocument: async (id: string) => {
      return await executeAPIRequest({
        method: "delete",
        url: `${ragUrl}/documents/${id}`,
        options: {
          replaceUrl: true
        }
      });
    },
    validateWorkflow: async (yamlText: string) => {
      const formData = new FormData();
      const yamlBlob = new Blob([yamlText], { type: "text/yaml" });
      formData.append("yaml_file", yamlBlob, "workflow.yaml");

      await executeAPIRequest<StatsResponse>({
        method: "post",
        url: `${workflowUrl}/workflows/yaml/validate`,
        options: {
          replaceUrl: true,
          data: formData
        }
      });

      return true;
    },
    runYamlWorkflow: async (yamlText: string, abortController) => {
      const formData = new FormData();
      const yamlBlob = new Blob([yamlText], { type: "text/yaml" });
      formData.append("yaml_file", yamlBlob, "workflow.yaml");

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response = await executeAPIRequest<any>({
        method: "post",
        url: `${workflowUrl}/workflows/yaml/run`,
        options: {
          replaceUrl: true,
          // responseType: "stream",
          // publicError: true,
          // adapter: "fetch",
          // timeout: 600000,
          abortController,
          data: formData
        }
      });
      return response;
    },
    getWorkflowDetails: async (workflowId: string) => {
      const response = await executeAPIRequest<WorkflowDetails>({
        method: "get",
        url: `${env.API_URL_INTELLIGENCE}/workflows/${workflowId}`,
        options: {
          replaceUrl: true
        }
      });
      return response;
    },
    createNewWorkflow: async (name: string, yamlFile: File, metadata: WorkflowMetadata) => {
      const formData = new FormData();
      const processPath = (path: File) => {
        const appendFile = (file: File, filename: string) =>
          formData.append(`yaml_file`, file, filename);
        if (path instanceof File) {
          appendFile(path, path.name);
        } else if ("path" in path && "name" in path) {
          console.warn("File path provided in browser environment. This is not supported.");
        }
      };
      formData.append("name", name);
      processPath(yamlFile);
      const metadataString = JSON.stringify(metadata);
      formData.append("metadata", metadataString);
      formData.append("name", name);
      const response = await executeAPIRequest<WorkflowDetails>({
        method: "post",
        url: `${env.API_URL_INTELLIGENCE}/workflows/new`,
        options: {
          replaceUrl: true,
          data: formData,
          transformRequest: [
            (data, headers) => {
              delete headers["Content-Type"];
              return data;
            }
          ]
        }
      });
      return response;
    },
    updateWorkflow: async (
      workflowId: string,
      { yamlFile, name, metadata }: { yamlFile?: File; name?: string; metadata?: WorkflowMetadata }
    ) => {
      const formData = new FormData();
      const processPath = (path: File) => {
        const appendFile = (file: File, filename: string) =>
          formData.append(`yaml_file`, file, filename);
        if (path instanceof File) {
          appendFile(path, path.name);
        } else if ("path" in path && "name" in path) {
          console.warn("File path provided in browser environment. This is not supported.");
        }
      };
      if (yamlFile) {
        processPath(yamlFile);
      }
      if (name) {
        formData.append("name", name);
      }
      if (metadata) {
        formData.append("metadata", JSON.stringify(metadata));
      }
      const response = await executeAPIRequest<null>({
        method: "patch",
        url: `${env.API_URL_INTELLIGENCE}/workflows/${workflowId}`,
        options: {
          replaceUrl: true,
          data: formData,
          transformRequest: [
            (data, headers) => {
              delete headers["Content-Type"];
              return data;
            }
          ]
        }
      });
      return response;
    },
    deleteWorkflow: async (workflowId: string) => {
      const response = await executeAPIRequest<null>({
        method: "delete",
        url: `${env.API_URL_INTELLIGENCE}/workflows/${workflowId}`,
        options: {
          replaceUrl: true
        }
      });
      return response;
    },
    getWorkflowFolders: async () => {
      const response = await executeAPIRequest<GetFoldersResponse>({
        method: "get",
        url: `${env.API_URL_INTELLIGENCE}/workflows/folders`,
        options: {
          replaceUrl: true
        }
      });
      return response;
    },
    getWorkflowFiles: async () => {
      const response = await executeAPIRequest<GetFilesResponse>({
        method: "get",
        url: `${env.API_URL_INTELLIGENCE}/workflows/list`,
        options: {
          replaceUrl: true
        }
      });
      return response;
    },
    setIsQuotaLimitDialogOpen: (value: boolean, mode?: QuotaDialogMode) => {
      if (value && (mode === "io-credits" || mode === "no-io-credits-warning")) {
        // Clear visit flag after 00:00 UTC (once per new UTC day)
        const todayUTC = new Date().toISOString().slice(0, 10);
        const visitedDateUTC = localStorage.getItem("isQuotaLimitDialogVisitedDateUTC");

        if (visitedDateUTC && visitedDateUTC !== todayUTC) {
          localStorage.removeItem("isQuotaLimitDialogVisited");
          localStorage.removeItem("isQuotaLimitDialogVisitedDateUTC");
        } else if (!visitedDateUTC) {
          // Backward compatibility: if legacy flag exists without date, reset it so daily behavior applies
          const legacyVisited = localStorage.getItem("isQuotaLimitDialogVisited");
          if (legacyVisited === "true") {
            localStorage.removeItem("isQuotaLimitDialogVisited");
          }
        }

        const isQuotaLimitDialogVisited = localStorage.getItem("isQuotaLimitDialogVisited");
        if (isQuotaLimitDialogVisited === "true") {
          return;
        }
      }
      set({ isQuotaLimitDialogOpen: value, quotaLimitDialogMode: mode || "no-io-credits" });
    },
    fetchIntelligenceInferenceStats: async () => {
      const response = await executeAPIRequest<IntelligenceInferenceStatsType>({
        method: "get",
        url: `${url}/inferences/statistics`,
        options: {
          replaceUrl: true,
          headers: {
            [GUEST_USER_ID_HEADER]: getGuestUserId()
          }
        }
      });
      return response;
    },
    fetchIntelligenceInferences: async () => {
      const response = await executeAPIRequest<{
        data: IntelligenceInferenceResponseType[];
        pagination: {
          total: number;
        };
      }>({
        method: "get",
        url: `${url}/inferences/?${stringify({
          page: 1,
          page_size: 100
        })}`,
        options: {
          replaceUrl: true,
          headers: {
            [GUEST_USER_ID_HEADER]: getGuestUserId()
          }
        }
      });

      const inferences = response.data.map(
        (item) =>
          ({
            status: item.status,
            inferenceId: item.inference_id,
            modelId: item.model_id,
            modelName: item.model_name,
            inputTokens: item.input_tokens,
            outputTokens: item.output_tokens,
            timestamp: item.timestamp,
            source: item.source
          }) as IntelligenceInferenceType
      );

      return inferences;
    },
    fetchIntelligenceUsage: async () => {
      const response = await executeAPIRequest<IntelligenceUsageType>({
        method: "get",
        url: `${url}/stats/token-credits-usage`,
        options: {
          replaceUrl: true
        }
      });
      set({ intelligenceUsage: response });
      return response;
    },
    getTotalCreditsUsed: (
      intelligenceUsage: IntelligenceUsageType,
      subscription: SubscriptionStatusResponse["data"]
    ) => {
      const dailyLimit = subscription?.daily_limit || 2;

      let totalUsed = "0";
      if (subscription?.subscription_details?.pricing_model === INTELLIGENCE_DEV_PRICE_MODEL) {
        totalUsed = intelligenceUsage?.total_credit_cost_current_window || "0";
      } else {
        totalUsed = intelligenceUsage?.total_credit_cost || "0";
      }
      return Math.min(parseFloat(totalUsed).toFixed(4) as unknown as number, dailyLimit);
    },
    fetchAttestation: async (data: AttestationRequest) => {
      const response = await executeAPIRequest<AttestationResponse>({
        method: "post",
        url: `${url}/private/attestation`,
        options: {
          replaceUrl: true,
          data: {
            ...data,
            nonce: data.nonce || generate32DigitHexNonce()
          }
        }
      });
      return response;
    }
  };
});

const normalizeStats = (data: StatsResponse) => {
  return {
    models: data.usage.find((item) => item.category === "chat")?.usage_per_model,
    api: data.usage.find((item) => item.category === "api_completions")?.usage_per_model
  } as Stats;
};

const normalizeModelDetail = (item: ModelDetailResponse) => {
  return {
    ...item,
    originalName: item.name,
    name: item.name.includes("/") ? item.name.split("/")[1] : item.name,
    id: item.model_id,
    createdAt: item.created_at,
    updatedAt: item.updated_at,
    chatQuotaLimit: item.chat_daily_quota_tier_1,
    apiQuotaLimit: item.api_completions_daily_quota_tier_1,
    supportsAttestation: item.supports_attestation,
    higherTierRequired: item.higher_tier_required,
    metadata: {
      ...item.metadata,
      shortDescription: item.metadata.short_description,
      contextLength: item.metadata.context_length,
      contextWindow: item.metadata.context_window,
      inputTokenPrice: parseFloat(item.metadata.input_token_price),
      outputTokenPrice: parseFloat(item.metadata.output_token_price),
      imageUrl: item.metadata.image_url || "/assets/images/AIModelLogo.png"
    }
  };
};

const normalizeMessage = (item: MessageResponse) => {
  return {
    ...item,
    id: item.message_id,
    chatId: item.chat_id
  } as MessageType;
};

const normaliseChunk = (chunk: ChunkResponse): RagChunk => {
  return {
    id: chunk.id,
    documentId: chunk.document_id,
    collectionIds: chunk.collection_ids,
    text: chunk.text,
    metadata: chunk.metadata,
    vector: chunk.metadata
  };
};

const normaliseDocument = (doc: RagDocumentResponse): RagFileDocument => {
  return {
    id: doc.id,
    collectionIds: doc.collection_ids,
    ownerId: doc.owner_id,
    documentType: doc.document_type,
    metadata: doc.metadata,
    title: doc.title,
    version: doc.version,
    sizeInBytes: doc.size_in_bytes,
    ingestionStatus: doc.ingestion_status as IngestionStatus,
    extractionStatus: doc.extraction_status as ExtractionStatus,
    createdAt: doc.created_at,
    updatedAt: doc.updated_at,
    ingestionAttemptNumber: doc.ingestion_attempt_number,
    summary: doc.summary,
    summaryEmbedding: doc.summary_embedding,
    totalTokens: doc.total_tokens
  };
};

const normalizeAgent = (item: AgentDetailResponse, type: string) => {
  const code = item.code_examples?.python || "";
  const curl = item.code_examples?.curl || "";

  return {
    ...item,
    code,
    curl,
    type,
    metadata: {
      ...item.metadata,
      imgUrl: item.metadata.image_url
    }
  } as AgentDetail;
};
